'use client';

import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import CreatePostPage from '../../components/create-post-page';
import { fetchGames } from '../../lib/game-config';
import { wikiGuidePostPath } from '../../lib/spa-routes';
import { useRegisteredGame } from '../use-registered-game';

type Channel = 'community' | 'guide' | 'edit';

type AvailableGame = { key: string; name: string; domain: string };

/**
 * SPA 发帖 / 发攻略 / 编辑已发布帖。
 * - `/:game/dcommunity/create`
 * - `/:game/dguides/create`
 * - `/:game/guide/:postId/edit`
 */
export default function SpaCreatePostPage({ channel }: { channel: Channel }) {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const gameParam = String(params.game || '');
  const postId = String(params.postId || '');
  const registered = useRegisteredGame(gameParam);
  const gameKey =
    registered.status === 'ready'
      ? registered.game.key || gameParam
      : gameParam;
  const gameDomain =
    registered.status === 'ready'
      ? registered.game.domain || gameParam
      : gameParam;
  const localName =
    registered.status === 'ready'
      ? registered.game.name || gameParam
      : gameParam;

  const [availableGames, setAvailableGames] = useState<AvailableGame[]>(() => [
    { key: gameKey, name: localName, domain: gameDomain },
  ]);

  useEffect(() => {
    let cancelled = false;
    void fetchGames(true)
      .then((list) => {
        if (cancelled) return;
        const mapped = list.map((item) => ({
          key: item.key,
          name: item.name || item.key,
          domain: item.domain || item.key,
        }));
        if (!mapped.some((item) => item.key === gameKey)) {
          mapped.unshift({ key: gameKey, name: localName, domain: gameDomain });
        }
        setAvailableGames(mapped);
      })
      .catch(() => {
        if (!cancelled) {
          setAvailableGames([{ key: gameKey, name: localName, domain: gameDomain }]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [gameDomain, gameKey, localName]);

  const gamesForPage = useMemo(() => {
    if (availableGames.some((item) => item.key === gameKey)) return availableGames;
    return [{ key: gameKey, name: localName, domain: gameDomain }, ...availableGames];
  }, [availableGames, gameDomain, gameKey, localName]);

  const presetRaw = searchParams.get('preset');
  const preset =
    presetRaw === 'announcement' || presetRaw === 'information'
      ? presetRaw
      : undefined;

  const [editMode, setEditMode] = useState<'post' | 'guide' | null>(
    channel === 'edit' ? null : channel === 'guide' ? 'guide' : 'post',
  );
  const [editError, setEditError] = useState<string | null>(null);

  useEffect(() => {
    if (channel !== 'edit' || registered.status !== 'ready' || !gameKey || !postId) return;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(
          `/api/games/${encodeURIComponent(gameKey)}/posts/${encodeURIComponent(postId)}`,
          { cache: 'no-store', credentials: 'same-origin' },
        );
        if (!res.ok) throw new Error('帖子不存在或已下线');
        const payload = (await res.json()) as { type?: string };
        if (!cancelled) {
          setEditMode(payload.type === 'guide' ? 'guide' : 'post');
          setEditError(null);
        }
      } catch (cause) {
        if (!cancelled) {
          setEditError(cause instanceof Error ? cause.message : '加载失败');
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [channel, gameKey, postId, registered.status]);

  if (!gameParam) {
    return (
      <main className="spa-boot" role="alert">
        <h1>缺少游戏标识</h1>
        <p>
          <a href="/">返回首页</a>
        </p>
      </main>
    );
  }

  if (registered.status === 'loading') {
    return (
      <main className="spa-boot" aria-busy="true">
        <p className="spa-boot-status" />
      </main>
    );
  }

  if (registered.status === 'error') {
    return (
      <main className="spa-boot" role="alert">
        <h1>加载失败</h1>
        <p>{registered.message}</p>
        <p>
          <a href="/">返回首页</a>
        </p>
      </main>
    );
  }

  if (channel === 'edit') {
    if (editError) {
      return (
        <main className="spa-boot" role="alert">
          <h1>加载失败</h1>
          <p>{editError}</p>
          <p>
            <a href={wikiGuidePostPath(gameDomain, postId)}>返回攻略</a>
          </p>
        </main>
      );
    }
    if (!editMode || !postId) {
      return (
        <main className="spa-boot" aria-busy="true">
          <p className="spa-boot-status" />
        </main>
      );
    }
    return (
      <CreatePostPage
        key={`${gameKey}:${postId}:edit`}
        mode={editMode}
        editPostId={postId}
        initialGameKey={gameKey}
        initialGameDomain={gameDomain}
        availableGames={gamesForPage}
        lockGame
      />
    );
  }

  const lockGame = channel === 'guide';
  return (
    <CreatePostPage
      key={`${gameKey}:${channel}:${preset || ''}`}
      mode={channel === 'guide' ? 'guide' : 'post'}
      preset={channel === 'community' ? preset : undefined}
      initialGameKey={gameKey}
      initialGameDomain={gameDomain}
      availableGames={gamesForPage}
      lockGame={lockGame}
    />
  );
}
